import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { FileImage, FileText, Loader2 } from "lucide-react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { api } from "../lib/apiClient";

// Render the live blueprint SVG to a high-res PNG data URL
async function svgToPng(scale = 2) {
  const svg = document.getElementById("blueprint-svg");
  if (!svg) throw new Error("Canvas not found");
  const clone = svg.cloneNode(true);
  const w = Number(svg.getAttribute("width"));
  const h = Number(svg.getAttribute("height"));
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const data = new XMLSerializer().serializeToString(clone);
  const url = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(data)));
  const img = new Image();
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  const canvas = document.createElement("canvas");
  canvas.width = w * scale; canvas.height = h * scale;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0B1017";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return { dataUrl: canvas.toDataURL("image/png"), w, h };
}

// Draw a vector floor-plan sheet for one floor
function drawFloorPlan(pdf, pw, ph, floor, title) {
  pdf.addPage();
  pdf.setFillColor(13, 17, 23); pdf.rect(0, 0, pw, ph, "F");
  pdf.setTextColor(240, 246, 252); pdf.setFontSize(16); pdf.text(title, 28, 34);
  const rooms = floor.rooms || [], items = floor.items || [];
  if (!rooms.length) { pdf.setTextColor(139, 148, 158); pdf.setFontSize(11); pdf.text("No rooms on this floor.", 28, 60); return; }
  const minX = Math.min(...rooms.map((r) => r.x)), minY = Math.min(...rooms.map((r) => r.y));
  const maxX = Math.max(...rooms.map((r) => r.x + r.w)), maxY = Math.max(...rooms.map((r) => r.y + r.h));
  const pad = 6, fw = (maxX - minX) + pad * 2, fh = (maxY - minY) + pad * 2;
  const area = { x: 28, y: 56, w: pw - 56, h: ph - 90 };
  const s = Math.min(area.w / fw, area.h / fh);
  const ox = area.x + (area.w - fw * s) / 2, oy = area.y + (area.h - fh * s) / 2;
  const X = (x) => ox + (x - (minX - pad)) * s, Y = (y) => oy + (y - (minY - pad)) * s;
  pdf.setDrawColor(201, 209, 217); pdf.setLineWidth(1);
  rooms.forEach((r) => {
    pdf.rect(X(r.x), Y(r.y), r.w * s, r.h * s);
    pdf.setTextColor(240, 246, 252); pdf.setFontSize(9); pdf.text(r.name || "Room", X(r.x) + 4, Y(r.y) + 12);
    pdf.setTextColor(139, 148, 158); pdf.setFontSize(7); pdf.text(`${r.w}' x ${r.h}' · ${Math.round(r.w * r.h)}ft²`, X(r.x) + 4, Y(r.y) + 22);
  });
  (floor.walls || []).forEach((w) => {
    pdf.setDrawColor(201, 209, 217); pdf.setLineWidth(Math.max(1, (w.thickness || 0.4) * s));
    pdf.line(X(w.x1), Y(w.y1), X(w.x2), Y(w.y2));
  });
  pdf.setLineWidth(1);
  const COL = { structural: [201, 209, 217], electrical: [56, 189, 248], plumbing: [52, 211, 153], hvac: [245, 158, 11], engineering: [244, 63, 94], furniture: [232, 121, 249] };
  items.forEach((it) => {
    const c = COL[it.layer] || [139, 148, 158];
    if (it.w && it.h) { pdf.setDrawColor(c[0], c[1], c[2]); pdf.setLineWidth(0.8); pdf.rect(X(it.x), Y(it.y), it.w * s, it.h * s); }
    else { pdf.setFillColor(c[0], c[1], c[2]); pdf.circle(X(it.x), Y(it.y), 2, "F"); }
  });

  // running dimension strings along the exterior (bottom = widths, left = depths)
  const uniq = (arr) => [...new Set(arr.map((v) => Math.round(v * 100) / 100))].sort((a, b) => a - b);
  const xs = uniq([minX, maxX, ...rooms.flatMap((r) => [r.x, r.x + r.w])]).filter((v) => v >= minX - 0.01 && v <= maxX + 0.01);
  const ys = uniq([minY, maxY, ...rooms.flatMap((r) => [r.y, r.y + r.h])]).filter((v) => v >= minY - 0.01 && v <= maxY + 0.01);
  pdf.setDrawColor(110, 118, 129); pdf.setLineWidth(0.5); pdf.setFontSize(7);
  const by = Y(maxY) + 16;
  pdf.setTextColor(139, 148, 158);
  pdf.line(X(xs[0]), by, X(xs[xs.length - 1]), by);
  xs.forEach((v) => pdf.line(X(v), by - 3, X(v), by + 3));
  for (let i = 0; i < xs.length - 1; i++) {
    const len = Math.round((xs[i + 1] - xs[i]) * 10) / 10;
    if (len > 0) pdf.text(`${len}'`, (X(xs[i]) + X(xs[i + 1])) / 2, by + 10, { align: "center" });
  }
  pdf.setTextColor(56, 189, 248);
  pdf.text(`${Math.round((maxX - minX) * 10) / 10}'-0" overall`, (X(xs[0]) + X(xs[xs.length - 1])) / 2, by + 20, { align: "center" });
  const lx = X(minX) - 16;
  pdf.setDrawColor(110, 118, 129); pdf.setTextColor(139, 148, 158);
  pdf.line(lx, Y(ys[0]), lx, Y(ys[ys.length - 1]));
  ys.forEach((v) => pdf.line(lx - 3, Y(v), lx + 3, Y(v)));
  for (let i = 0; i < ys.length - 1; i++) {
    const len = Math.round((ys[i + 1] - ys[i]) * 10) / 10;
    if (len > 0) pdf.text(`${len}'`, lx - 5, (Y(ys[i]) + Y(ys[i + 1])) / 2, { align: "center", angle: 90 });
  }
  pdf.setTextColor(56, 189, 248);
  pdf.text(`${Math.round((maxY - minY) * 10) / 10}'-0" overall`, lx - 15, (Y(ys[0]) + Y(ys[ys.length - 1])) / 2, { align: "center", angle: 90 });
}

// Per-floor room schedule (names + areas)
function drawRoomSchedule(pdf, pw, ph, flrs) {
  pdf.addPage();
  pdf.setFillColor(13, 17, 23); pdf.rect(0, 0, pw, ph, "F");
  pdf.setTextColor(240, 246, 252); pdf.setFontSize(16); pdf.text("Room Schedule", 28, 34);
  pdf.setTextColor(139, 148, 158); pdf.setFontSize(9); pdf.text("Per-floor room names and areas", 28, 50);
  let y = 74, grand = 0;
  const newPageIf = (need) => { if (y > ph - need) { pdf.addPage(); pdf.setFillColor(13, 17, 23); pdf.rect(0, 0, pw, ph, "F"); y = 44; } };
  flrs.forEach((f, fi) => {
    const rooms = f.rooms || [];
    newPageIf(70);
    pdf.setTextColor(56, 189, 248); pdf.setFontSize(12); pdf.text(f.name || `Floor ${fi + 1}`, 28, y); y += 16;
    pdf.setTextColor(139, 148, 158); pdf.setFontSize(8);
    pdf.text("ROOM", 40, y); pdf.text("SIZE", 320, y); pdf.text("AREA", 440, y); y += 4;
    pdf.setDrawColor(48, 54, 61); pdf.setLineWidth(0.5); pdf.line(28, y, pw - 28, y); y += 12;
    let sub = 0;
    rooms.forEach((r) => {
      newPageIf(40);
      const a = Math.round(r.w * r.h); sub += a;
      pdf.setTextColor(240, 246, 252); pdf.setFontSize(9); pdf.text(String(r.name || "Room"), 40, y);
      pdf.setTextColor(139, 148, 158); pdf.text(`${r.w}' x ${r.h}'`, 320, y); pdf.text(`${a} ft²`, 440, y);
      y += 14;
    });
    grand += sub;
    pdf.setDrawColor(48, 54, 61); pdf.line(28, y - 4, pw - 28, y - 4);
    pdf.setTextColor(52, 211, 153); pdf.setFontSize(9);
    pdf.text(`${f.name || ("Floor " + (fi + 1))} subtotal: ${sub} ft²  ·  ${rooms.length} rooms`, 40, y + 8);
    y += 28;
  });
  newPageIf(30);
  pdf.setTextColor(56, 189, 248); pdf.setFontSize(12);
  pdf.text(`Total conditioned area: ${grand} ft²`, 28, y + 6);
}

// Bill of Materials sheet from the estimate result
function drawMaterials(pdf, pw, ph, result) {
  pdf.addPage();
  pdf.setFillColor(13, 17, 23); pdf.rect(0, 0, pw, ph, "F");
  pdf.setTextColor(240, 246, 252); pdf.setFontSize(16); pdf.text("Bill of Materials", 28, 34);
  pdf.setTextColor(139, 148, 158); pdf.setFontSize(9);
  pdf.text(`Auto takeoff from plan geometry · Tier ${result.tier} · Materials total $${(result.materials_total || 0).toLocaleString()}`, 28, 50);
  let y = 74;
  const newPageIf = (need) => { if (y > ph - need) { pdf.addPage(); pdf.setFillColor(13, 17, 23); pdf.rect(0, 0, pw, ph, "F"); y = 44; } };
  const byCat = {};
  (result.materials || []).forEach((m) => { (byCat[m.category] = byCat[m.category] || []).push(m); });
  Object.entries(byCat).forEach(([cat, items]) => {
    newPageIf(70);
    pdf.setTextColor(56, 189, 248); pdf.setFontSize(12); pdf.text(cat, 28, y); y += 16;
    pdf.setTextColor(139, 148, 158); pdf.setFontSize(8);
    pdf.text("MATERIAL", 40, y); pdf.text("QTY", 330, y); pdf.text("UNIT $", 420, y); pdf.text("COST", 510, y); y += 4;
    pdf.setDrawColor(48, 54, 61); pdf.setLineWidth(0.5); pdf.line(28, y, pw - 28, y); y += 12;
    items.forEach((m) => {
      newPageIf(40);
      pdf.setTextColor(240, 246, 252); pdf.setFontSize(9); pdf.text(String(m.item), 40, y);
      pdf.setTextColor(139, 148, 158);
      pdf.text(`${m.qty.toLocaleString()} ${m.unit}`, 330, y);
      pdf.text(`$${m.unit_cost.toLocaleString()}`, 420, y);
      pdf.setTextColor(240, 246, 252); pdf.text(`$${m.cost.toLocaleString()}`, 510, y);
      y += 14;
    });
    y += 12;
  });
  newPageIf(30);
  pdf.setDrawColor(48, 54, 61); pdf.line(28, y - 4, pw - 28, y - 4);
  pdf.setTextColor(56, 189, 248); pdf.setFontSize(12);
  pdf.text(`Materials total: $${(result.materials_total || 0).toLocaleString()}`, 28, y + 12);
}

export default function ExportModal({ open, onOpenChange, project }) {
  const [busy, setBusy] = useState(false);

  const exportPng = async () => {
    setBusy(true);
    try {
      const { dataUrl } = await svgToPng(2);
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `${project.name || "blueprint"}.png`; a.click();
      toast.success("PNG exported");
    } catch { toast.error("Export failed"); } finally { setBusy(false); }
  };

  const exportPdf = async () => {
    setBusy(true);
    try {
      const { dataUrl, w, h } = await svgToPng(2);
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const flrs = (project.floors && project.floors.length) ? project.floors : [project.blueprint];

      // ---- Cover sheet (A0) ----
      pdf.setFillColor(13, 17, 23); pdf.rect(0, 0, pw, ph, "F");
      pdf.setTextColor(56, 189, 248); pdf.setFontSize(12); pdf.text("ARCHIMESH AI", 28, 60);
      pdf.setTextColor(240, 246, 252); pdf.setFontSize(34); pdf.text(project.name || "Blueprint", 28, 120);
      const totalAreaAll = flrs.reduce((s, f) => s + (f.rooms || []).reduce((a, r) => a + r.w * r.h, 0), 0);
      pdf.setFontSize(12); pdf.setTextColor(139, 148, 158);
      pdf.text("Construction Document Set", 28, 150);
      pdf.text(`City context: ${project.city || "Custom"}`, 28, 175);
      pdf.text(`Storeys: ${flrs.length}    Total area: ${Math.round(totalAreaAll)} ft²`, 28, 193);
      if (project.site && project.site.slope_type && project.site.slope_type !== "Flat")
        pdf.text(`Site: ${project.site.slope_type} @ ${project.site.slope_pct}% grade`, 28, 211);
      pdf.text(`Generated ${new Date().toLocaleDateString()}`, 28, 229);
      pdf.setTextColor(139, 148, 158); pdf.setFontSize(10); pdf.text("Sheet index", 28, 268);
      let sidx = 286;
      pdf.text("A0   Cover", 40, sidx); sidx += 16;
      pdf.text("A1   Presentation Plan", 40, sidx); sidx += 16;
      flrs.forEach((f, i) => { pdf.text(`A${2 + i}   Floor Plan — ${f.name || "Floor " + (i + 1)}`, 40, sidx); sidx += 16; });
      pdf.text(`A${2 + flrs.length}   Room Schedule`, 40, sidx); sidx += 16;
      pdf.text(`A${3 + flrs.length}   Bill of Materials`, 40, sidx); sidx += 16;
      pdf.text(`A${4 + flrs.length}   Site Plan`, 40, sidx);
      pdf.addPage();

      pdf.setFillColor(13, 17, 23); pdf.rect(0, 0, pw, ph, "F");
      // Title block
      pdf.setTextColor(240, 246, 252); pdf.setFontSize(18);
      pdf.text(`ArchiMesh AI — ${project.name || "Blueprint"}`, 28, 34);
      pdf.setFontSize(10); pdf.setTextColor(139, 148, 158);
      pdf.text(`City context: ${project.city || "Custom"}   |   Generated ${new Date().toLocaleDateString()}`, 28, 50);
      pdf.text(`Total area: ${Math.round((project.blueprint?.rooms || []).reduce((s, r) => s + r.w * r.h, 0))} ft²   |   Rooms: ${(project.blueprint?.rooms || []).length}`, 28, 64);
      // Blueprint image
      const margin = 28; const top = 80;
      const availW = pw - margin * 2; const availH = ph - top - margin;
      const ratio = Math.min(availW / w, availH / h);
      const iw = w * ratio; const ih = h * ratio;
      pdf.addImage(dataUrl, "PNG", margin + (availW - iw) / 2, top, iw, ih);
      // Legend
      pdf.setFontSize(8);
      const legend = [["Electrical", [56, 189, 248]], ["Plumbing", [52, 211, 153]], ["HVAC", [245, 158, 11]]];
      let lx = margin;
      legend.forEach(([lbl, c]) => { pdf.setTextColor(c[0], c[1], c[2]); pdf.text(`● ${lbl}`, lx, ph - 14); lx += 70; });

      // ---- Per-floor plan sheets (A2..) ----
      flrs.forEach((f, i) => drawFloorPlan(pdf, pw, ph, f, `Floor Plan — ${f.name || "Floor " + (i + 1)}`));

      // ---- Room schedule ----
      drawRoomSchedule(pdf, pw, ph, flrs);

      // ---- Bill of Materials (fetch estimate) ----
      try {
        const est = await api.post("/estimate/cost", {
          blueprint: project.blueprint, tier: project.site?.cost_tier || "Standard", region_multiplier: 1.0,
          stories: flrs.length, slope_type: project.site?.slope_type || "Flat", slope_pct: project.site?.slope_pct || 0,
          floors: flrs, lot: project.lot || null,
        });
        if (est.data?.materials?.length) drawMaterials(pdf, pw, ph, est.data);
      } catch { /* materials sheet optional */ }

      // ---- Site Plan sheet ----
      const rooms = project.blueprint?.rooms || [];
      if (rooms.length) {
        pdf.addPage();
        pdf.setFillColor(13, 17, 23); pdf.rect(0, 0, pw, ph, "F");
        pdf.setTextColor(240, 246, 252); pdf.setFontSize(18);
        pdf.text(`Site Plan — ${project.name || "Blueprint"}`, 28, 34);
        pdf.setFontSize(10); pdf.setTextColor(139, 148, 158);
        pdf.text("Lot outline (solid) · Setback line (dashed) · Building footprint (blue)", 28, 50);

        const minX = Math.min(...rooms.map((r) => r.x)), minY = Math.min(...rooms.map((r) => r.y));
        const maxX = Math.max(...rooms.map((r) => r.x + r.w)), maxY = Math.max(...rooms.map((r) => r.y + r.h));
        const setback = 10, lotPad = 22;
        const lotFW = (maxX - minX) + lotPad * 2, lotFH = (maxY - minY) + lotPad * 2;
        const area = { x: 28, y: 70, w: pw - 56, h: ph - 110 };
        const s = Math.min(area.w / lotFW, area.h / lotFH);
        const ox = area.x + (area.w - lotFW * s) / 2, oy = area.y + (area.h - lotFH * s) / 2;
        const FX = (fx) => ox + (fx - (minX - lotPad)) * s;
        const FY = (fy) => oy + (fy - (minY - lotPad)) * s;

        pdf.setDrawColor(139, 148, 158); pdf.setLineWidth(1.2);
        pdf.rect(FX(minX - lotPad), FY(minY - lotPad), lotFW * s, lotFH * s);
        pdf.setDrawColor(245, 158, 11); pdf.setLineWidth(0.8); pdf.setLineDashPattern([4, 3], 0);
        pdf.rect(FX(minX - lotPad + setback), FY(minY - lotPad + setback), (lotFW - 2 * setback) * s, (lotFH - 2 * setback) * s);
        pdf.setLineDashPattern([], 0);
        pdf.setDrawColor(56, 189, 248); pdf.setLineWidth(1);
        rooms.forEach((r) => pdf.rect(FX(r.x), FY(r.y), r.w * s, r.h * s, "S"));

        // Driveway from street to garage (follows the slope)
        const garage = rooms.find((r) => /garage|carport/i.test(r.name)) ||
          rooms.reduce((a, b) => (b.y + b.h > a.y + a.h ? b : a), rooms[0]);
        const gx = garage.x + garage.w / 2;
        const gyFront = garage.y + garage.h;
        const streetY = maxY + lotPad;
        pdf.setDrawColor(110, 118, 129); pdf.setLineWidth(Math.max(3, 9 * s)); pdf.setLineCap("round");
        pdf.line(FX(gx), FY(gyFront), FX(gx), FY(streetY));
        pdf.setLineWidth(1); pdf.setLineCap("butt");
        pdf.setTextColor(139, 148, 158); pdf.setFontSize(8);
        pdf.text("STREET", FX(minX - lotPad) + (lotFW * s) / 2, FY(streetY) + 12, { align: "center" });
        const slopeTxt = (project.site && project.site.slope_type && project.site.slope_type !== "Flat")
          ? ` (follows ${project.site.slope_pct}% slope)` : "";
        pdf.text("Driveway" + slopeTxt, FX(gx) + 7, (FY(gyFront) + FY(streetY)) / 2);

        // Spot elevations + cut/fill balance (grading plan)
        const cxF = (minX + maxX) / 2, cyF = (minY + maxY) / 2;
        const k = (project.site && project.site.slope_type && project.site.slope_type !== "Flat") ? (project.site.slope_pct / 100) : 0;
        const E = (x, y) => {
          if (!k) return 0;
          if (project.site.slope_type === "Cross-slope") return k * (cyF - y);
          if (project.site.slope_type === "Uphill") return k * (x - cxF);
          return k * (cxF - x);
        };
        const corners = [[minX - lotPad, minY - lotPad], [maxX + lotPad, minY - lotPad], [maxX + lotPad, maxY + lotPad], [minX - lotPad, maxY + lotPad]];
        pdf.setTextColor(56, 189, 248); pdf.setFontSize(8);
        corners.forEach(([x, y]) => {
          const e = E(x, y);
          pdf.setFillColor(56, 189, 248); pdf.circle(FX(x), FY(y), 1.6, "F");
          pdf.text(`${e >= 0 ? "+" : ""}${e.toFixed(1)}'`, FX(x) + 3, FY(y) + (y < cyF ? 10 : -3));
        });
        if (k) {
          const dropG = k * ((project.site.slope_type === "Cross-slope") ? (maxY - minY) : (maxX - minX));
          const cy = ((maxX - minX) * (maxY - minY) * dropG / 2) / 27;
          pdf.setTextColor(139, 148, 158); pdf.setFontSize(9);
          pdf.text(`Grading balance:  Cut ~${Math.round(cy / 2)} cy  ·  Fill ~${Math.round(cy / 2)} cy  ·  Net ~ balanced  (spot elevations in ft, datum = pad)`, 28, ph - 16);

          // Rainwater drainage: runoff arrows (downhill) + swale on low edge
          const st = project.site.slope_type;
          const drawArrow = (x1, y1, x2, y2) => {
            pdf.line(x1, y1, x2, y2);
            const ang = Math.atan2(y2 - y1, x2 - x1), hl = 5;
            pdf.line(x2, y2, x2 - hl * Math.cos(ang - 0.4), y2 - hl * Math.sin(ang - 0.4));
            pdf.line(x2, y2, x2 - hl * Math.cos(ang + 0.4), y2 - hl * Math.sin(ang + 0.4));
          };
          pdf.setDrawColor(56, 189, 248); pdf.setLineWidth(0.9);
          if (st === "Cross-slope") {
            [0.3, 0.5, 0.7].forEach((f) => { const x = (minX - lotPad) + lotFW * f; drawArrow(FX(x), FY(cyF) - 14, FX(x), FY(cyF) + 14); });
            pdf.setLineDashPattern([3, 2], 0); pdf.line(FX(minX - lotPad), FY(maxY + lotPad - 2), FX(maxX + lotPad), FY(maxY + lotPad - 2)); pdf.setLineDashPattern([], 0);
          } else {
            const toRight = st === "Downhill";
            [0.3, 0.5, 0.7].forEach((f) => { const y = (minY - lotPad) + lotFH * f; const xa = FX(cxF) - 16, xb = FX(cxF) + 16; toRight ? drawArrow(xa, FY(y), xb, FY(y)) : drawArrow(xb, FY(y), xa, FY(y)); });
            const lowX = toRight ? maxX + lotPad - 2 : minX - lotPad + 2;
            pdf.setLineDashPattern([3, 2], 0); pdf.line(FX(lowX), FY(minY - lotPad), FX(lowX), FY(maxY + lotPad)); pdf.setLineDashPattern([], 0);
          }
          pdf.setTextColor(56, 189, 248); pdf.setFontSize(8);
          pdf.text("Runoff arrows (downhill) · drainage swale (dashed) on low edge", 28, ph - 30);
        }

        pdf.setFontSize(8); pdf.setTextColor(245, 158, 11);
        pdf.text(`Setback ${setback}'`, FX(minX - lotPad + setback) + 4, FY(minY - lotPad + setback) + 12);
        pdf.setTextColor(139, 148, 158);
        pdf.text(`Lot ${Math.round(lotFW)}' x ${Math.round(lotFH)}'`, FX(minX - lotPad) + 4, FY(maxY + lotPad) - 6);

        const site = project.site;
        if (site && site.slope_type && site.slope_type !== "Flat" && site.slope_pct > 0) {
          const cxF = (minX + maxX) / 2, cyF = (minY + maxY) / 2;
          let x1, y1, x2, y2;
          if (site.slope_type === "Cross-slope") { x1 = FX(cxF); y1 = FY(minY - lotPad + 4); x2 = FX(cxF); y2 = FY(maxY + lotPad - 4); }
          else if (site.slope_type === "Uphill") { x1 = FX(maxX + lotPad - 4); y1 = FY(cyF); x2 = FX(minX - lotPad + 4); y2 = FY(cyF); }
          else { x1 = FX(minX - lotPad + 4); y1 = FY(cyF); x2 = FX(maxX + lotPad - 4); y2 = FY(cyF); }
          pdf.setDrawColor(244, 63, 94); pdf.setLineWidth(1.5); pdf.line(x1, y1, x2, y2);
          pdf.setFillColor(244, 63, 94); pdf.circle(x2, y2, 3, "F");
          pdf.setTextColor(244, 63, 94); pdf.setFontSize(9);
          pdf.text(`Slope down ${site.slope_pct}% (${site.slope_type})`, (x1 + x2) / 2, (y1 + y2) / 2 - 6, { align: "center" });
        }
      }

      pdf.save(`${project.name || "blueprint"}.pdf`);
      toast.success("PDF report exported");
    } catch { toast.error("Export failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Export Blueprint</DialogTitle>
          <DialogDescription className="text-[#8B949E]">Download your blueprint as a high-res image or a PDF report.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <button data-testid="export-png-btn" onClick={exportPng} disabled={busy}
            className="flex flex-col items-center gap-2 p-6 rounded-xl border border-[#30363D] bg-[#0D1117] hover:border-[#38BDF8] transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-7 h-7 animate-spin text-[#38BDF8]" /> : <FileImage className="w-7 h-7 text-[#38BDF8]" />}
            <span className="text-sm font-medium">PNG Image</span>
            <span className="text-xs text-[#6E7681]">High-res 2×</span>
          </button>
          <button data-testid="export-pdf-btn" onClick={exportPdf} disabled={busy}
            className="flex flex-col items-center gap-2 p-6 rounded-xl border border-[#30363D] bg-[#0D1117] hover:border-[#34D399] transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-7 h-7 animate-spin text-[#34D399]" /> : <FileText className="w-7 h-7 text-[#34D399]" />}
            <span className="text-sm font-medium">PDF Report</span>
            <span className="text-xs text-[#6E7681]">With title block</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
